/**
 * Pomocniki rozliczeń.
 *
 * Kwoty przychodzą z API jako tekst, żeby nie stracić precyzji na liczbach
 * zmiennoprzecinkowych. Formatujemy je do wyświetlenia, ale nigdy nie liczymy
 * po stronie przeglądarki — sumy i wynagrodzenia wylicza serwer, inaczej
 * ekran i raport do wypłat mogłyby pokazywać różne wartości.
 */

import type { MonthTally } from '@/api/types'

const KWOTA = new Intl.NumberFormat('pl-PL', {
  style: 'currency',
  currency: 'PLN',
})

const MIESIACE = [
  'styczeń',
  'luty',
  'marzec',
  'kwiecień',
  'maj',
  'czerwiec',
  'lipiec',
  'sierpień',
  'wrzesień',
  'październik',
  'listopad',
  'grudzień',
]

/** Kwota z API. Puste pole to „stawka nieustalona”, a nie zero złotych. */
export function formatAmount(value: string | null): string {
  if (value === null) return '—'
  const liczba = Number(value)
  return Number.isFinite(liczba) ? KWOTA.format(liczba) : '—'
}

export function formatRate(value: string | null): string {
  return value === null ? 'nie ustalono' : `${formatAmount(value)}/h`
}

/** „2.25” → „2 godz. 15 min”. Same godziny dziesiętne czyta się źle. */
export function formatMinutes(minutes: number): string {
  const godziny = Math.floor(minutes / 60)
  const reszta = minutes % 60
  if (godziny === 0) return `${reszta} min`
  if (reszta === 0) return `${godziny} godz.`
  return `${godziny} godz. ${reszta} min`
}

export function monthName(month: number): string {
  return MIESIACE[month - 1] ?? String(month)
}

export function formatMonthLabel({ year, month }: Pick<MonthTally, 'year' | 'month'>): string {
  return `${monthName(month)} ${year}`
}

/** Poprzedni miesiąc — jedyny, który na pewno da się już zamknąć. */
export function previousMonth(today = new Date()): { year: number; month: number } {
  const pierwszy = new Date(today.getFullYear(), today.getMonth(), 1)
  pierwszy.setDate(0)
  return { year: pierwszy.getFullYear(), month: pierwszy.getMonth() + 1 }
}
